import { Deployments } from 'types';
import { canSignUpForDeployment } from 'util/user';

import Button from '@mui/material/Button';
import Modal2 from 'components/_shared/Modal2';
import { makeStyles } from '@mui/styles';

const HEADING_TITLE = 'Select a deployment';
const HEADING_DESCRIPTION = 'Please select the insurance or provider group that this patient\'s Membership will be a part of';

const useStyles = makeStyles(() => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '32px'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        '& button': {
            padding: '16px 48px',
            width: '100%'
        }
    },
    deploymentButton: {
        border: '1px solid #E3E3FA',
        borderRadius: '16px',
        boxShadow: '0px 8px 16px 4px #0000000D',
        fontSize: '18px',
        padding: '20px',
        justifyContent: 'space-between',
        textTransform: 'none',
        '& i': {
            color: '#999DFF'
        },
        '& span': {
            color: '#1F2152',
            fontWeight: 600
        }
    },
    deploymentButtonDisabled: {
        boxShadow: 'none',
        border: '1px solid #E3E3FA80',
        cursor: 'not-allowed',
        '& i': {
            color: '#A6A6BF'
        },
        '& span': {
            color: '#A6A6BF',
            fontWeight: 600
        }
    },
    deploymentSelectorContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    heading: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        paddingBottom: '24px',
        '& h2': {
            color: '#062D7A',
            margin: 0
        },
        '& span': {
            color: '#5A6A81',
            fontWeight: 300,
            maxWidth: '75%',
            textAlign: 'center'
        }
    },
    disabledText: {
        border: '1px solid #A6A6BF40',
        backgroundColor: '#E3E3FA80',
        borderRadius: '100px',
        padding: '4px 12px',
        color: '#A6A6BF80',
        fontWeight: 500,
        fontSize: '14px',
        maxWidth: '50%',
        textAlign: 'center'
    }
}));

type DeploymentSelectorProps = {
    onClose: () => void;
    deployments: Deployments[];
    onSelectDeployment: (deployment: Deployments) => void;
};

const DeploymentSelector: React.FC<DeploymentSelectorProps> = ({
    onClose,
    deployments,
    onSelectDeployment,
}) => {
    const classes = useStyles();

    const handleDeploymentClick = (deployment: Deployments) => {
        const signUpAvailabilityResult = canSignUpForDeployment(deployment);
        if (signUpAvailabilityResult.canSignUp) {
            onSelectDeployment(deployment);
        }
    };

    return (
        <Modal2
            onClose={onClose}
            title={HEADING_TITLE}
        >
            <div className={classes.body}>
                <div className={classes.deploymentSelectorContainer}>
                    <div className={classes.heading}>
                        <h2>{HEADING_TITLE}</h2>
                        <span>{HEADING_DESCRIPTION}</span>
                    </div>
                    {deployments.map((deployment) => {
                        const signUpAvailabilityResult = canSignUpForDeployment(deployment);
                        return (
                            <Button
                                key={deployment.id}
                                aria-label={
                                    !signUpAvailabilityResult.canSignUp ? `${deployment.name} is not available: ${signUpAvailabilityResult.errorMessage?.longReason || signUpAvailabilityResult.errorMessage?.shortReason || ''}` : `Select ${deployment.name}`
                                }
                                className={`${classes.deploymentButton} ${!signUpAvailabilityResult.canSignUp ? classes.deploymentButtonDisabled : ''}`}
                                disabled={!signUpAvailabilityResult.canSignUp}
                                onClick={() => handleDeploymentClick(deployment)}
                            >
                                <span>{deployment.name}</span>
                                {signUpAvailabilityResult.canSignUp && (
                                    <i className="far fa-arrow-right" />
                                )}
                                {!signUpAvailabilityResult.canSignUp && (signUpAvailabilityResult.errorMessage?.shortReason || signUpAvailabilityResult.errorMessage?.longReason) && (
                                    <div className={classes.disabledText}>{signUpAvailabilityResult.errorMessage?.shortReason || signUpAvailabilityResult.errorMessage?.longReason}</div>
                                )}
                            </Button>
                        );
                    })}
                </div>
            </div>
        </Modal2>
    );
};

export default DeploymentSelector;
